import { config } from '../config';

/**
 * Utilitários para processamento de URLs de avatar
 */

export interface AvatarUrlOptions {
  addTimestamp?: boolean;
  crossOrigin?: boolean;
}

/**
 * Processa URL de avatar baseado no tipo de URL
 * @param avatarUrl URL original do avatar
 * @param options Opções de processamento
 * @returns URL processada ou null se inválida
 */
export const processAvatarUrl = (avatarUrl: string | null, options: AvatarUrlOptions = {}): string | null => {
  if (!avatarUrl || avatarUrl.trim() === '') {
    return null;
  }

  const { addTimestamp = true } = options;

  // Se a URL for relativa (começando com /assets ou /api/v1), adicionar o base URL do WhatsApp
  if (avatarUrl.startsWith('/api/v1') || avatarUrl.startsWith('/assets')) {
    // Normalizamos a URL do backend (removemos a barra final se existir)
    const baseUrl = config.WHATSAPP_API_URL ? config.WHATSAPP_API_URL.replace(/\/api\/v1\/?$/, '') : 'http://127.0.0.1:8081';
    avatarUrl = `${baseUrl}${avatarUrl}`;
  }

  // Para URLs do Supabase com assinatura, não modificar
  if (avatarUrl.includes('supabase.co') && avatarUrl.includes('sign')) {
    return avatarUrl;
  }

  // Para base64, retornar como está
  if (avatarUrl.startsWith('data:image')) {
    return avatarUrl;
  }

  // Para URLs normais, adicionar timestamp se solicitado
  if (addTimestamp) {
    const separator = avatarUrl.includes('?') ? '&' : '?';
    return `${avatarUrl}${separator}t=${new Date().getTime()}`;
  }

  return avatarUrl;
};

/**
 * Verifica se uma URL é uma URL pré-assinada do Supabase
 * @param url URL para verificar
 * @returns true se for uma URL pré-assinada do Supabase
 */
export const isSupabaseSignedUrl = (url: string): boolean => {
  return url.includes('supabase.co') && url.includes('sign') && url.includes('token=');
};

/**
 * Verifica se uma URL de avatar ainda é válida (não expirada)
 * @param url URL para verificar
 * @returns Promise<boolean> indicando se a URL é válida
 */
export const validateAvatarUrl = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, {
      method: 'HEAD',
      mode: 'cors'
    });
    return response.ok;
  } catch (error) {
    console.warn('Erro ao validar URL do avatar:', error);
    return false;
  }
};

/**
 * Gera initials para fallback do avatar
 * @param name Nome da pessoa
 * @returns String com as iniciais
 */
export const generateInitials = (name: string): string => {
  if (!name || name.trim() === '') {
    return '?';
  }

  return name
    .trim()
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Hook para lidar com erro de carregamento de avatar
 * @param avatarUrl URL do avatar
 * @param onError Callback chamado quando há erro
 * @returns Função para ser usada no onError da imagem
 */
export const createAvatarErrorHandler = (
  avatarUrl: string,
  onError?: (error: Event) => void
) => {
  return (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Erro ao carregar avatar:', {
      src: avatarUrl,
      error: e,
      naturalWidth: e.currentTarget.naturalWidth,
      naturalHeight: e.currentTarget.naturalHeight
    });

    const target = e.currentTarget;
    target.onerror = null; // Prevent infinite loop

    // Se for uma URL do Supabase, tentar recarregar uma vez
    if (avatarUrl.includes('supabase.co') && !avatarUrl.includes('retry=1')) {
      const retryUrl = avatarUrl.includes('?')
        ? `${avatarUrl}&retry=1`
        : `${avatarUrl}?retry=1`;
      target.src = retryUrl;
    } else {
      // Ocultar imagem como fallback
      target.style.display = 'none';
    }

    // Chamar callback de erro personalizado
    if (onError) {
      onError(e.nativeEvent);
    }
  };
};

/**
 * Configurações padrão para elementos img de avatar
 */
export const getAvatarImageProps = (
  avatarUrl: string,
  alt: string,
  onError?: (error: Event) => void
) => {
  return {
    src: processAvatarUrl(avatarUrl),
    alt,
    crossOrigin: 'anonymous' as const,
    loading: 'eager' as const,
    onError: createAvatarErrorHandler(avatarUrl, onError)
  };
}; 